import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/shadcn/ui/button';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { calculateBtcTransactionSize } from '@/networks/btc-like/common';
import { ResultUtxo } from '@/networks/btc-like/common/transactions/api';
import {
  UtxoSelectModalInfoElement,
  UtxoSelectModalInfoElementSatoshis,
} from '@/pages/Swap/components/SwapPanel/SwapButton/SwapButtonBtc/components/UtxoSelectModalInfoElement';
import { UtxoSelectModalInfoSelectFee } from '@/pages/Swap/components/SwapPanel/SwapButton/SwapButtonBtc/components/UtxoSelectModalInfoSelectFee';
import { cn } from '@/utils';

import { useBtcUtxoMergeTransaction } from './hooks';

interface HeaderWalletDrawerBalanceCardBtcUtxoMergerInfoProps {
  onChangeVbyteEstimate: (vbyteEstimate: number | undefined) => void;
  onToggleDisabled: (isDisabled: boolean) => void;
  onToggleFiatPrice: (shouldShowFiatPrice: boolean) => void;
  selectedUtxos: ResultUtxo[];
  shouldShowFiatPrice: boolean;
  vbyteEstimate: number | undefined;
}

const HeaderWalletDrawerBalanceCardBtcUtxoMergerInfo: React.FC<HeaderWalletDrawerBalanceCardBtcUtxoMergerInfoProps> = ({
  onChangeVbyteEstimate,
  onToggleDisabled,
  onToggleFiatPrice,
  selectedUtxos,
  shouldShowFiatPrice,
  vbyteEstimate,
}) => {
  const { createMergeUtxosTransaction } = useBtcUtxoMergeTransaction();

  const { data, isFetching } = useQuery({
    queryKey: ['extendPsbtWithSwapBtc', selectedUtxos, vbyteEstimate],
    queryFn: () => createMergeUtxosTransaction(selectedUtxos, vbyteEstimate),
    placeholderData: (a) => a,
    enabled: Array.isArray(selectedUtxos),
  });

  const { tx, inputAmount, uncoveredValue, txMinerFeeByVSize, mempoolTip } = data || {};

  const outputAmount = uncoveredValue && uncoveredValue < 0n ? uncoveredValue : (tx?.['outputs']?.[0]?.amount ?? 0n);

  const { adjustedVSize = 0 } = tx ? calculateBtcTransactionSize(tx) : {};

  useEffect(() => {
    const isOutputAmountValid = outputAmount > 0n;
    onToggleDisabled(!isOutputAmountValid);
  }, [isFetching]);

  return (
    <div className="bg-foreground flex flex-col gap-6 overflow-hidden rounded-lg p-2">
      <div className="flex flex-col items-center justify-between gap-6 sm:flex-row">
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="secondary"
            className="font-roboto flex h-8 cursor-pointer items-center gap-2 px-3 py-2 text-sm font-normal"
            onClick={() => onToggleFiatPrice(!shouldShowFiatPrice)}
          >
            <div className="flex items-center gap-2 text-xs">
              <Checkbox
                className="size-3"
                checkedIconClassName="size-3"
                checked={shouldShowFiatPrice}
                onCheckedChange={() => onToggleFiatPrice(!shouldShowFiatPrice)}
              />
              Show in Fiat
            </div>
          </Button>
        </div>

        <div className="flex items-center gap-2">{isFetching && <Loader2 className="size-4 animate-spin" />}</div>
      </div>

      <div
        className={cn(
          'grid grid-cols-1 gap-2',
          '[@media(min-width:350px)]:grid-cols-2',
          '[@media(min-width:500px)]:grid-cols-3',
          '[@media(min-width:768px)]:grid-cols-4'
        )}
      >
        <UtxoSelectModalInfoElementSatoshis
          label="Input Amount"
          value={inputAmount ?? 0n}
          shouldShowFiatPrice={shouldShowFiatPrice}
        />
        <UtxoSelectModalInfoElementSatoshis
          className={cn({ 'text-red-500': inputAmount && inputAmount > 0n && outputAmount <= 0n })}
          label="Output Amount"
          value={outputAmount}
          shouldShowFiatPrice={shouldShowFiatPrice}
        />

        <UtxoSelectModalInfoElement label="Tx Size" value={`${adjustedVSize} vBytes`} />

        <UtxoSelectModalInfoElementSatoshis
          label="Tx Miner Fee"
          value={txMinerFeeByVSize ?? 0n}
          shouldShowFiatPrice={shouldShowFiatPrice}
        />
        <UtxoSelectModalInfoElementSatoshis
          label="Mempool Tip"
          value={mempoolTip ?? 0n}
          shouldShowFiatPrice={shouldShowFiatPrice}
        />
        <UtxoSelectModalInfoElementSatoshis
          label="Tx Total Fee"
          value={(mempoolTip ?? 0n) + (txMinerFeeByVSize ?? 0n)}
          shouldShowFiatPrice={shouldShowFiatPrice}
        />
        <UtxoSelectModalInfoElement
          label="Fee Rate"
          value={
            <UtxoSelectModalInfoSelectFee vbyteEstimate={vbyteEstimate} onChangeVbyteEstimate={onChangeVbyteEstimate} />
          }
        />
      </div>
    </div>
  );
};

export default HeaderWalletDrawerBalanceCardBtcUtxoMergerInfo;
